import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { fetchMe, logout as apiLogout } from "../lib/api";
import { clearAllCaches } from "../lib/swr-cache";

const AuthContext = createContext(null);
const USER_CACHE_KEY = "nerdy_user";

function readCachedUser() {
    try {
        const raw = localStorage.getItem(USER_CACHE_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
}

export const AuthProvider = ({ children }) => {
    // Optimistic boot: render instantly with the last known user, re-validate
    // /auth/me in the background. Only a REAL 401 signs the user out — network
    // blips keep the cached session (offline-tolerant PWA behavior).
    const [user, setUserState] = useState(readCachedUser);
    const [loading, setLoading] = useState(() => !readCachedUser());

    const setUser = useCallback((next) => {
        setUserState(next);
        try {
            if (next) localStorage.setItem(USER_CACHE_KEY, JSON.stringify(next));
            else localStorage.removeItem(USER_CACHE_KEY);
        } catch {
            // storage blocked — non-fatal
        }
    }, []);

    const checkAuth = useCallback(async () => {
        try {
            const { data } = await fetchMe();
            setUser(data);
        } catch (err) {
            if (err?.response?.status === 401) {
                setUser(null); // session truly gone
            } else if (!readCachedUser()) {
                // First-time visitor and the check failed — treat as signed out.
                setUser(null);
            } else {
                console.warn("[auth] /me check failed (keeping cached session):", err?.message || err);
            }
        } finally {
            setLoading(false);
        }
    }, [setUser]);

    useEffect(() => {
        // If returning from OAuth callback, skip the /me check.
        // AuthCallback will exchange the session_id and establish the session first.
        if (window.location.hash?.includes("session_id=")) {
            setLoading(false);
            return;
        }
        checkAuth();
    }, [checkAuth]);

    const signOut = async () => {
        try { await apiLogout(); } catch (err) {
            console.warn("[auth] logout API failed; clearing local session anyway:", err?.message || err);
        }
        clearAllCaches();
        setUser(null);
        window.location.href = "/";
    };

    const value = useMemo(
        () => ({ user, setUser, loading, refresh: checkAuth, signOut }),
        [user, loading, checkAuth, setUser] // eslint-disable-line react-hooks/exhaustive-deps
    );

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
